(function() {
  
  const initTabbedSlider = (container) => {
    if (!container || container.dataset.tabbedInitialized === 'true') return;
    
    const tabs = container.querySelectorAll('[data-tab-index]');
    const panels = container.querySelectorAll('[data-panel-index]');
    const panelsWrapper = container.querySelector('.tabbed-product-slider__container');
    
    if (!tabs.length || !panels.length) return;
    
    container.dataset.tabbedInitialized = 'true';
    
    // Wait for images to load before measuring
    const waitForImages = (callback) => {
      const images = container.querySelectorAll('img');
      if (!images.length) {
        callback();
        return;
      }
      
      let loadedCount = 0;
      let callbackFired = false;
      const totalImages = images.length;
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalImages && !callbackFired) {
          callbackFired = true;
          // Wait a bit more for layout to settle after images load
          requestAnimationFrame(() => {
            requestAnimationFrame(callback);
          });
        }
      };
      
      images.forEach(img => {
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete, { once: true });
          img.addEventListener('error', checkComplete, { once: true });
        }
      });
      
      // Fallback timeout in case some images never load
      setTimeout(() => {
        if (!callbackFired) {
          callbackFired = true;
          callback();
        }
      }, 1000);
    };
    
    // Equalize product card heights across all tabs (works on both desktop and mobile)
    const equalizeCardHeights = (skipVisible = false) => {
      // Find all product card containers (items) in all panels within this container
      const allItems = container.querySelectorAll('.horizontal-slider__item');
      if (!allItems.length) return;
      
      // If we already have min-heights set and we're skipping visible panels, 
      // just use the existing max height
      let maxHeight = 0;
      if (skipVisible) {
        allItems.forEach(item => {
          const existingHeight = item.style.minHeight ? parseFloat(item.style.minHeight) : item.offsetHeight;
          if (existingHeight > maxHeight) {
            maxHeight = existingHeight;
          }
        });
      } else {
        // Store original panel display styles
        const panelStates = new Map();
        panels.forEach(panel => {
          panelStates.set(panel, {
            display: panel.style.display || '',
            position: panel.style.position || '',
            left: panel.style.left || '',
            visibility: panel.style.visibility || '',
            opacity: panel.style.opacity || '',
            wasActive: panel.classList.contains('is-active')
          });
        });
        
        // Temporarily make all panels visible but positioned off-screen for measurement
        panels.forEach(panel => {
          panel.style.display = 'block';
          panel.style.position = 'absolute';
          panel.style.left = '-9999px';
          panel.style.visibility = 'hidden';
          panel.style.opacity = '0';
        });
        
        // Force a reflow to ensure measurements are accurate
        container.offsetHeight;
        
        // Measure all item heights (which contain the cards)
        allItems.forEach(item => {
          const height = item.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Restore original panel states
        panels.forEach(panel => {
          const state = panelStates.get(panel);
          panel.style.display = state.display;
          panel.style.position = state.position;
          panel.style.left = state.left;
          panel.style.visibility = state.visibility;
          panel.style.opacity = state.opacity;
        });
      }
      
      // Apply the maximum height to all items if we found a height > 0
      if (maxHeight > 0) {
        allItems.forEach(item => {
          item.style.minHeight = maxHeight + 'px';
        });
      }
    };
    
    // Update container height to match active panel to avoid layout jumps
    const updateContainerHeight = () => {
      if (!panelsWrapper) return;
      const activePanel = panelsWrapper.querySelector('.tabs__panel.is-active') || panelsWrapper.querySelector('.tabs__panel');
      if (!activePanel) return;

      const targetHeight = activePanel.offsetHeight;
      if (targetHeight > 0) {
        panelsWrapper.style.height = targetHeight + 'px';
      }
    };
    
    // Function to reinitialize slider when panel becomes visible
    const reinitializeSlider = (panel) => {
      const slider = panel.querySelector('[data-horizontal-slider]');
      if (!slider) return;
      
      // Check if global functions are available
      if (typeof window.cleanupHorizontalSlider === 'function' && 
          typeof window.initializeHorizontalSlider === 'function') {
        
        // Cleanup existing slider
        window.cleanupHorizontalSlider(slider);
        
        // Wait for panel to be fully rendered with display: block
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Reinitialize slider
            window.initializeHorizontalSlider(slider);
            
            // Update chevrons after initialization
            setTimeout(() => {
              updateChevrons(panel);
              updateContainerHeight();
            }, 50);
            
            // Also check after a longer delay to ensure everything is rendered
            setTimeout(() => {
              updateChevrons(panel);
            }, 200);
          });
        });
      }
    };
    
    // Tab switching
    tabs.forEach((tab) => {
      tab.addEventListener('click', function(e) {
        e.preventDefault();
        
        const index = this.dataset.tabIndex;
        
        // Skip if already active
        if (this.classList.contains('is-active')) return;
        
        // Update tabs
        tabs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        this.classList.add('is-active');
        this.setAttribute('aria-selected', 'true');
        
        // Update panels
        panels.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-hidden', 'true');
        });
        
        const activePanel = container.querySelector('[data-panel-index="' + index + '"]');
        if (activePanel) {
          // Make panel visible first
          activePanel.classList.add('is-active');
          activePanel.setAttribute('aria-hidden', 'false');
          
          // Set container height immediately using the previous equalized heights
          // This prevents layout shift before we re-measure
          if (panelsWrapper && panelsWrapper.style.height) {
            // Keep existing height temporarily
          } else {
            // Set initial height
            const tempHeight = activePanel.offsetHeight;
            if (tempHeight > 0) {
              panelsWrapper.style.height = tempHeight + 'px';
            }
          }
          
          // Reinitialize slider
          reinitializeSlider(activePanel);
          
          // Update container height immediately
          updateContainerHeight();
          
          // Only re-equalize after images load if heights might have changed
          // But do it in a way that doesn't cause visible shifts
          waitForImages(() => {
            // Use existing min-heights as baseline to prevent flinching
            equalizeCardHeights(true);
            updateContainerHeight();
          });
        }
      });
    });
    
    // Chevron navigation
    const setupChevrons = (panel) => {
      const prevBtn = panel.querySelector('[data-slider-prev]');
      const nextBtn = panel.querySelector('[data-slider-next]');
      const viewport = panel.querySelector('[data-horizontal-slider-viewport]');
      
      if (!prevBtn || !nextBtn || !viewport) return;
      
      const scrollAmount = 360;
      
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        viewport.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
      
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        viewport.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
      
      viewport.addEventListener('scroll', function() {
        updateChevrons(panel);
      });
      
      // Initial check
      setTimeout(function() {
        updateChevrons(panel);
      }, 200);
    };
    
    const updateChevrons = (panel) => {
      const prevBtn = panel.querySelector('[data-slider-prev]');
      const nextBtn = panel.querySelector('[data-slider-next]');
      const viewport = panel.querySelector('[data-horizontal-slider-viewport]');
      const track = panel.querySelector('[data-horizontal-slider-track]');
      const slider = panel.querySelector('[data-horizontal-slider]');
      
      if (!prevBtn || !nextBtn || !viewport || !track) return;
      
      const scrollLeft = viewport.scrollLeft;
      const maxScroll = track.scrollWidth - viewport.clientWidth;
      const hasOverflow = track.scrollWidth > viewport.clientWidth + 1;
      
      // Add/remove no-overflow class for border styling
      if (slider) {
        if (hasOverflow) {
          slider.classList.remove('no-overflow');
        } else {
          slider.classList.add('no-overflow');
        }
      }
      
      if (hasOverflow) {
        if (scrollLeft > 5) {
          prevBtn.classList.add('is-visible');
        } else {
          prevBtn.classList.remove('is-visible');
        }
        
        if (scrollLeft < maxScroll - 5) {
          nextBtn.classList.add('is-visible');
        } else {
          nextBtn.classList.remove('is-visible');
        }
      } else {
        prevBtn.classList.remove('is-visible');
        nextBtn.classList.remove('is-visible');
      }
    };
    
    // Setup chevrons for all panels
    panels.forEach(function(panel) {
      setupChevrons(panel);
      // Initial check for overflow state
      setTimeout(function() {
        updateChevrons(panel);
      }, 200);
    });
    
    // Equalize card heights on initialization - wait for images to load first
    waitForImages(() => {
      equalizeCardHeights();
      updateContainerHeight();
    });
    
    // Initial height
    setTimeout(updateContainerHeight, 200);
    
    // Handle resize
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function() {
        const activePanel = container.querySelector('[data-panel-index].is-active');
        if (activePanel) {
          updateChevrons(activePanel);
        }
        // Also update all panels to ensure overflow state is correct
        panels.forEach(function(panel) {
          updateChevrons(panel);
        });
        equalizeCardHeights();
        updateContainerHeight();
      }, 100);
    });
  };
  
  // Initialize on load
  const initAll = () => {
    const containers = document.querySelectorAll('[data-tabbed-slider]');
    containers.forEach(function(container) {
      initTabbedSlider(container);
    });
  };
  
  // Run initialization - make sure horizontal slider is loaded first
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  
  // Also run after a delay to ensure horizontal slider script has loaded
  setTimeout(initAll, 150);
  
  // Handle Shopify theme editor
  document.addEventListener('shopify:section:load', function(event) {
    const container = event.target.querySelector('[data-tabbed-slider]');
    if (container) {
      container.dataset.tabbedInitialized = 'false';
      setTimeout(() => {
        initTabbedSlider(container);
      }, 100);
    }
  });
})();